import { z } from "zod";

// Upload result schema.
const UploadResultSchema = z.object({
	url: z.string().nullable(),
	error: z.string().optional(),
});

// Expiration options.
export const ExpirationOptions = {
	HOUR: "60min",
	DAY: "24hours",
	WEEK: "1week",
	UNLIMITED: "unlimited",
} as const;

// Expiration type schema.
export const ExpirationTypeSchema = z.enum([
	ExpirationOptions.HOUR,
	ExpirationOptions.DAY,
	ExpirationOptions.WEEK,
	ExpirationOptions.UNLIMITED,
]);

// Input schema.
export const CreateGaussianSplatUploadInputSchema = z.object({
	title: z.string().min(1).max(100).optional(),
	description: z.string().max(1000).optional(),
	thumbnailUrl: z.string().optional(),
	modelFileUrl: z.union([z.string(), UploadResultSchema]).optional(),
	modelType: z.string().optional(),
	fileSizeBytes: z.number().int().min(0).optional(),
	expirationType: ExpirationTypeSchema,
	password: z.string().min(3).max(200).optional(),
});

// Gaussian Splat upload schema.
export const GaussianSplatUploadSchema = z.object({
	shareId: z
		.string()
		.min(5)
		.max(32)
		.regex(/^[0-9a-z]+$/),
	title: z.string().min(1).max(100).optional(),
	description: z.string().max(1000).optional(),
	thumbnailUrl: z.string().optional(),
	modelFileUrl: z.string().optional(),
	modelType: z.string().optional(),
	fileSizeBytes: z.number().int().min(0).optional(),
	expiresAt: z.date().optional(),
	viewCount: z.number().int().min(0).default(0),
});

// Backward-compatible aliases.
export const CreateOrderInputSchema = CreateGaussianSplatUploadInputSchema;
export const OrderSchema = GaussianSplatUploadSchema;

// Types.
export type CreateGaussianSplatUploadInput = z.infer<typeof CreateGaussianSplatUploadInputSchema>;
export type GaussianSplatUpload = z.infer<typeof GaussianSplatUploadSchema>;
export type ExpirationType = z.infer<typeof ExpirationTypeSchema>;

// Backward-compatible type aliases.
export type CreateOrderInput = CreateGaussianSplatUploadInput;
export type Order = GaussianSplatUpload;

// Validation helpers.
export const validateCreateOrderInput = (data: unknown): CreateOrderInput => {
	return CreateOrderInputSchema.parse(data);
};

export const validateOrder = (data: unknown): Order => {
	return OrderSchema.parse(data);
};

export const extendExpirationDate = (
	currentExpiresAt: Date | string | null | undefined,
	expirationType: ExpirationType,
	now = new Date(),
): Date => {
	if (expirationType === ExpirationOptions.UNLIMITED) {
		return new Date(2074, 3, 30);
	}

	const current = currentExpiresAt ? new Date(currentExpiresAt) : null;
	const currentTime = current && Number.isFinite(current.getTime()) ? current.getTime() : 0;
	const baseTime = Math.max(now.getTime(), currentTime);

	switch (expirationType) {
		case ExpirationOptions.HOUR:
			return new Date(baseTime + 60 * 60 * 1000);
		case ExpirationOptions.DAY:
			return new Date(baseTime + 24 * 60 * 60 * 1000);
		case ExpirationOptions.WEEK:
			return new Date(baseTime + 7 * 24 * 60 * 60 * 1000);
	}

	throw new Error("Unsupported expiration type");
};

// Expiration calculation.
export const calculateExpirationDate = (expirationType: ExpirationType): Date | null => {
	const now = new Date();
	switch (expirationType) {
		case ExpirationOptions.HOUR:
			return new Date(now.getTime() + 60 * 60 * 1000);
		case ExpirationOptions.DAY:
			return new Date(now.getTime() + 24 * 60 * 60 * 1000);
		case ExpirationOptions.WEEK:
			return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
		case ExpirationOptions.UNLIMITED:
			return new Date(2074, 3, 30);
	}
};
